'use client'

import { Fragment, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ComingUpItem } from '@repo/dashboard/ComingUpItem'
import { ComingUpDateSeparator } from '@repo/dashboard/ComingUpDateSeparator'
import { useUpcomingDatesStore } from '@repo/store/useUpcomingDatesStore'
import { DashboardPanel } from '@repo/dashboard/DashboardPanel'
import { KeyDateIcon } from '@repo/ui/KeyDateIcon'
import { EmptyState } from '@repo/ui/EmptyState'
import { ComingUpSettingsForm } from '@repo/dashboard/ComingUpSettingsForm'
import { ComingUpContext } from '@repo/dashboard/ComingUpContext'

const panelOptions = [
  { label: 'All', value: 'all' },
  { label: 'Key dates', value: 'keyDates' },
  { label: 'Matter reviews', value: 'matterReviews' }
]

export const ComingUpTimeline = ({
  fetchUpcomingDates_DI,
  matterWorkspaceVisible,
  clientNo,
  matterNo
}: ComingUpTimelineProps) => {
  const { dashboard, workspace, setClientMatter, fetchUpcomingDates, setUpcomingDatesMode } = useUpcomingDatesStore()
  const settings = matterWorkspaceVisible ? workspace : dashboard
  const { upcomingDates, upcomingDatesModeLabel, upcomingDatesModeValue } = settings

  useEffect(() => {
    if (matterWorkspaceVisible && clientNo && matterNo) {
      setClientMatter({ clientNo, matterNo })
    }

    fetchUpcomingDates_DI ? fetchUpcomingDates_DI() : fetchUpcomingDates({ isMatterSpecific: matterWorkspaceVisible })
  }, [fetchUpcomingDates, fetchUpcomingDates_DI, matterWorkspaceVisible, clientNo, matterNo, upcomingDatesModeValue])

  return (
    <ComingUpContext.Provider value={matterWorkspaceVisible ? 'workspace' : 'dashboard'}>
      <DashboardPanel
        title={`Coming up${upcomingDatesModeLabel === 'All' ? '' : `: ${upcomingDatesModeLabel}`}`}
        options={panelOptions}
        selectedOption={upcomingDatesModeValue}
        onOptionSelected={({ label, value }: any) => {
          setUpcomingDatesMode({
            isMatterSpecific: matterWorkspaceVisible,
            upcomingDatesModeLabel: label,
            upcomingDatesModeValue: value
          })
        }}
        settingsPopup={matterWorkspaceVisible ? null : ComingUpSettingsForm}
      >
        {!upcomingDates || !Object.keys(upcomingDates).length ? (
          <div data-testid="ComingUpTimeline">
            <EmptyState
              text="No upcoming dates to show"
              icon={<KeyDateIcon sx={{ fontSize: 56, mb: 4 }} />}
              sx={{ height: `calc(100% - 48px)` }}
            />
          </div>
        ) : (
          <div
            data-testid="ComingUpTimeline"
            tabIndex={0}
            style={{
              height: '600px',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <div
              style={{ backgroundColor: 'white', width: '100%', height: '24px', position: 'sticky', top: 0, zIndex: 4 }}
            ></div>

            <div
              style={{
                marginTop: '-24px',
                minHeight: '30rem',
                overflowY: 'auto',
                maxHeight: matterWorkspaceVisible ? '485px' : '590px',
                zIndex: 4,
                position: 'relative'
              }}
            >
              {Object.keys(upcomingDates).map((groupedDate) => {
                const dates = upcomingDates[groupedDate]

                return (
                  <Fragment key={groupedDate}>
                    <ComingUpDateSeparator title={groupedDate} sx={{ position: 'sticky', top: '24px', zIndex: 5 }} />

                    {dates.map((date: any, index: number) => (
                      <ComingUpItem
                        key={`${groupedDate}-${date.clientNo}-${date.matterNo}-${index}-${date.keyDateId || date.matterReviewDate}`}
                        item={date}
                        isMatterSpecific={matterWorkspaceVisible}
                      />
                    ))}
                  </Fragment>
                )
              })}
            </div>
          </div>
        )}
      </DashboardPanel>
    </ComingUpContext.Provider>
  )
}

type ComingUpTimelineProps = {
  fetchUpcomingDates_DI?: () => void
  matterWorkspaceVisible?: boolean
  clientNo?: string
  matterNo?: string
}

ComingUpTimeline.propTypes = {
  fetchUpcomingDates_DI: PropTypes.func,
  matterWorkspaceVisible: PropTypes.bool,
  clientNo: PropTypes.string,
  matterNo: PropTypes.string
}
